/**
 * AI Helper API client.
 */

import apiClient from './client';

const DEFAULT_SERVER_ERROR = 'Errore del server.';
const isObject = (value) => value != null && typeof value === 'object' && !Array.isArray(value);

const normalizeAIHelperPayload = (data) => {
  if (!isObject(data)) {
    return data;
  }

  const payload = isObject(data.ai_helper) ? { ...data, ...data.ai_helper } : data;
  const balanceFromResources = payload?.app_resources?.gettoncini?.balance;

  if (typeof payload.balance === 'number' || typeof balanceFromResources !== 'number') {
    return payload;
  }

  return {
    ...payload,
    balance: balanceFromResources,
  };
};

function normalizeAIHelperError(error) {
  if (!error || !error.response) {
    return { error: 'NETWORK_ERROR', message: 'Errore di rete.' };
  }

  const { status } = error.response;
  const data = normalizeAIHelperPayload(error.response.data);
  if (data && data.error && data.message) {
    const normalizedError = { ...data };
    if (typeof data.balance === 'number') {
      normalizedError.balance = data.balance;
    }
    return normalizedError;
  }
  if (typeof data?.detail === 'string') {
    return { error: 'HTTP_ERROR', message: data.detail };
  }
  if (Array.isArray(data?.detail)) {
    const message = data.detail.map((e) => e?.msg).filter(Boolean).join('; ') || DEFAULT_SERVER_ERROR;
    return { error: 'HTTP_ERROR', message };
  }
  return { error: 'HTTP_ERROR', message: `HTTP ${status || 'ERROR'}` };
}

/**
 * Request an AI helper explanation.
 *
 * Returns the response data on success: { response_text, cache_hit, balance }.
 * Throws an error object on failure: { error, message, balance? }.
 *
 * @param {string} puzzleInstanceId
 * @param {object} selection - structured component selection
 * @param {string} selectedText - raw highlighted text (display only)
 * @param {string} token - Clerk JWT token
 * @returns {Promise<{response_text: string, cache_hit: boolean, balance: number}>}
 */
export async function requestAIHelp(puzzleInstanceId, selection, selectedText, token) {
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    const response = await apiClient.post(
      '/ai-helper/request',
      {
        puzzle_instance_id: puzzleInstanceId,
        selection,
        selected_text: (selectedText || '').slice(0, 200),
      },
      { headers }
    );
    const data = normalizeAIHelperPayload(response.data);
    if (data?.error) {
      throw data;
    }
    return data;
  } catch (error) {
    const normalized = normalizeAIHelperError(error);
    throw normalized;
  }
}

/**
 * Fetch paginated AI helper history.
 */
export async function fetchAIHelperHistory(token, { limit = 20, offset = 0 } = {}) {
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  try {
    const response = await apiClient.get('/ai-helper/history', {
      headers,
      params: { limit, offset },
    });
    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    throw new Error(`HTTP ${status || 'ERROR'}`);
  }
}

/**
 * Fetch a single AI helper entry with full response text.
 */
export async function fetchAIHelperEntry(entryId, token) {
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  try {
    const response = await apiClient.get(`/ai-helper/entries/${entryId}`, { headers });
    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    throw new Error(`HTTP ${status || 'ERROR'}`);
  }
}
